import { OrgId } from "./Org.schema";
import { OrgRegistrationPackageId, RegistrationPackageLineItem } from "./OrgRegistrationPackage.schema";
import { OrgPaymentPlanId } from "./OrgPaymentPlan.schema";
import { OrgPaymentInvoice } from "./OrgPayment.schema";
import { PlayerId } from "./Player.schema";
import { PlayerBundleId } from "./PlayerBundle.schema";
import { AccountId, OrgCouponId, OrgSeasonId } from "..";

export type OrgInvoiceId = string;
type OrgInvoiceBase = {
  id: OrgInvoiceId;
  invoiceGroupId: OrgInvoiceId; //If parent invoice, this will be equal to the invoice id. Otherwise, if the invoice is a child, it is the parent invoice id.
  memo: string;
  orgId: OrgId;
  playerBundleId: PlayerBundleId;
  createdAtMS: number;
  dueDateMS: number;
  amountDueCents: number;
  // Will be set differently depending on the type of invoice. Can come from registration package or it's defined on the manul invoice
  lateFeeCentsToBeIssuedIfLate: number;
  // Derived from amountCents on OrgPayment for THIS specific invoice, including credits and actual payments.
  // DOES NOT include late fees or processing fees.
  // DOES NOT include children payments on any children invoices the invoice may have
  // DOES include all successful payments, both credits and actual money.
  derivedTotalAmountPaidCentsBeforeAllFees: number;
  thisInvoicePaidInFullDateMS: number;
  previousFailedPaymentAttemptMS?: number;
  failedPaymentScheduledRetryMS?: number; //DEPRECATED. Pretty sure this isn't used anymore
  numberOfFailedPaymentAttempts?: number; //DEPRECATED. Pretty sure this isn't used anymore
  // Phase 2 (Aug 2026): last SCHEDULED-attempt failure, recorded at charge time so the cron can make
  // classification-driven retry decisions without extra reads. Absent on invoices that have never
  // failed a scheduled attempt (and on all attempts made before Phase 2 shipped) — absent means the
  // engine behaves exactly as before (fail-open).
  lastScheduledPaymentFailure?: {
    code: string; // NMI cc response code ("202") or NACHA return code ("R01")
    rail: "cc" | "ck";
    paymentMethodId: string;
    atMS: number;
  };
  // Phase 2: the invoice's charge-cycle counter. Incremented whenever a previously-collected
  // payment is undone and the invoice legitimately needs a fresh REAL charge — an eCheck return
  // (NMI webhook) or an admin deleting a credit that had marked it paid. Participates in the
  // payment idempotency keys so the new charge can never be satisfied by the cached NMI success of
  // the undone payment.
  chargeCycleCount?: number;
  // Phase 2: set when a charge attempt for this invoice died with an UNKNOWN outcome (transport
  // error mid-request — NMI may or may not have processed it) and we could not confirm either way.
  // While present, the next charge attempt queries NMI by the stored orderId BEFORE charging: if a
  // transaction is found, the attempt is blocked and ops alerted (prefer a missed charge over a
  // double charge); if nothing is found, the flag clears and the charge proceeds normally.
  lastUnknownOutcomeCharge?: {
    atMS: number;
    orderId: string;
  };
};

type OrgInvoiceParentBase = {
  autoChargeDateMS?: number; //If an e-check returns, then we have to set an auto charge date
  accountIdScheduledToPay?: AccountId; //If an e-check returns, then we have to specify who should be repaying this
  //Equals amountDueCents except if children invoices have been created
  derivedTotalAmountDueCentsIncludingChildrenInvoices: number;
  // Includes credits and actual payments. Does NOT include fees. Equals parentInvoice.derivedTotalAmountPaidCentsBeforeAllFees + allChildrenInvoices.derivedTotalAmountPaidCentsBeforeAllFees
  derivedTotalAmountPaidCentsIncludingChildrenInvoices: number;
  appliedCouponCodeDetails?: {
    orgCouponCodeSnapshot: string;
    orgCouponIdSnapshot: OrgCouponId;
    discountAmountCents: number;
  };
};

type OrgInvoicePaymentPlanInstallmentBase = {
  autoChargeDateMS: number;
  orgPaymentPlanId: OrgPaymentPlanId;
  parentOrgInvoiceId: OrgInvoiceId;
  accountIdScheduledToPay: string;
};

export enum OrgInvoiceTypes {
  manual = "manual",
  manualPaymentPlanInstallment = "manual-payment-plan-installment",
  registration = "registration",
  registrationPaymentPlanInstallment = "registration-payment-plan-installment"
}

export type OrgInvoice__Manual = OrgInvoiceBase &
  OrgInvoiceParentBase & {
    type: OrgInvoiceTypes.manual;
    allowedPaymentPlanIds?: Record<OrgPaymentPlanId, true>;
    assignedByAccountId: AccountId;
    // Optional breakdown of amountDueCents, keyed by label like registration package line items. When present
    // the amounts add up to amountDueCents. Display only: charging, payment plans, late fees and credits all
    // use amountDueCents.
    lineItems?: Record<string, RegistrationPackageLineItem>;
    // The admin can keep the breakdown off what the family sees (pay page, app and email)
    hideLineItemsFromParents?: boolean;
  };

export type OrgInvoice__Registration = OrgInvoiceBase &
  OrgInvoiceParentBase & {
    type: OrgInvoiceTypes.registration;
    orgSeasonId: OrgSeasonId;
    orgRegistrationPackageId: OrgRegistrationPackageId;
  };

export type OrgInvoice__ManualPaymentPlanInstallment = OrgInvoiceBase &
  OrgInvoicePaymentPlanInstallmentBase & {
    type: OrgInvoiceTypes.manualPaymentPlanInstallment;
  };

export type OrgInvoice__RegistrationPaymentPlanInstallment = OrgInvoiceBase &
  OrgInvoicePaymentPlanInstallmentBase & {
    type: OrgInvoiceTypes.registrationPaymentPlanInstallment;
    orgRegistrationPackageId: OrgRegistrationPackageId;
    orgSeasonId: OrgSeasonId;
  };

export type OrgInvoiceParent = OrgInvoice__Registration | OrgInvoice__Manual;

export type OrgInvoiceChild = OrgInvoice__RegistrationPaymentPlanInstallment | OrgInvoice__ManualPaymentPlanInstallment;

export type OrgInvoice = OrgInvoiceParent | OrgInvoiceChild;

export type OrgInvoiceParentWithChildAndPaymentData = {
  parentOrgInvoice: OrgInvoiceParent;
  childrenOrgInvoices: OrgInvoiceChild[];
  orgPayments: OrgPaymentInvoice[];
};

export type ParentInvoiceData = {
  due_date_ms: string;
  created_at_ms: string;
  id: string;
  org_id: string;
  total_invoiced: string;
  total_paid: string;
  total_balance_remaining: string;
  memo: string;
  num_invoices: string;
  num_paid_invoices: string;
  status: OverallInvoiceStatus;
  player_name: string;
  item: OrgInvoice__Manual;
  assigned_by_first_name: string;
  assigned_by_last_name: string;
};

export enum OverallInvoiceStatus {
  paid = "paid",
  latePaymentInstallment = "latePaymentInstallment",
  invoiceFailedPayment = "invoiceFailedPayment",
  inProgress = "inProgress",
  late = "late",
  outstanding = "outstanding"
}
